import { Component, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { TEMPLATE_COLUMNS, TEMPLATE_ACTIONS } from '../data/constants/templates.constants';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ApiTemplatesService } from '../data/services/api/api-templates.service';
import { TemplateService } from '../data/services/view/template.service';

@Component({
  selector: 'app-templates-list',
  templateUrl: './templates-list.component.html',
  styleUrls: ['./templates-list.component.scss'],
  providers: [MessageService, ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TemplatesListComponent {
  columns = TEMPLATE_COLUMNS;
  actions = TEMPLATE_ACTIONS;
  createEntity = false;
  selectedTemplateId: string | null = null;
  action = 'CREATE';
  
  // Error handling
  hasError = false;
  errorMessage = '';
  
  // Loading & Empty states
  isLoading = false;
  emptyMessage = 'No hay plantillas creadas. Crea tu primera plantilla para comenzar';
  
  additionalMainActions = [
    { label: 'Nueva Plantilla', icon: 'fa-solid fa-square-plus', action: 'CREATE' }
  ];

  constructor(
    public apiTemplatesService: ApiTemplatesService,
    public templateService: TemplateService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {}

  hideDialog() {
    this.createEntity = false;
    this.selectedTemplateId = null;
    this.action = 'CREATE';
    this.cdr.markForCheck();
  }

  mainActions(flow: string) {
    switch (flow) {
      case 'CREATE':
        this.templateService.newEntity();
        this.createEntity = true;
        this.action = 'CREATE';
        this.cdr.markForCheck();
        break;
    }
  }

  handleTableAction(event: any) {
    const { action, data } = event;
    
    switch (action) {
      case 'EDIT':
        this.editTemplate(data._id);
        break;
      case 'ACTIVATE':
        this.activateTemplate(data._id);
        break;
      case 'DEACTIVATE':
        this.deactivateTemplate(data._id);
        break;
      case 'DELETE':
        this.deleteTemplate(data._id);
        break;
    }
  }

  editTemplate(id: string) {
    this.selectedTemplateId = id;
    this.action = 'EDIT';
    this.isLoading = true;
    
    this.apiTemplatesService.getCollection(id).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        
        if (!response.error) {
          this.templateService.loadEntityForEdit(response.data);
          this.createEntity = true;
          this.cdr.detectChanges(); // Forzar detección de cambios
        } else {
          this.handleError(new Error(response.msg));
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.handleError(error);
      }
    });
  }

  saveForm() {
    // Primero validar variables antes de enviar
    if (!this.templateService.validateVariables()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error de Validación',
        detail: 'Hay variables usadas en los templates que no están definidas en la lista de variables'
      });
      return;
    }
    
    if (!this.templateService.entity.valid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Incompleto',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    // Limpiar templates opcionales usando el método del servicio
    const data = this.templateService.cleanTemplateData(this.templateService.entity);
    
    console.log('📤 Enviando datos al backend:', {
      action: this.action,
      data: data,
      templateId: this.selectedTemplateId
    });
    
    if (this.action === 'CREATE') {
      this.apiTemplatesService.postCollection(data, 'create').subscribe({
        next: (r: any) => {
          if (r?.error) {
            return this.messageService.add({ 
              severity: 'error', 
              summary: 'Error', 
              detail: r.msg || 'Error al crear la plantilla'
            });
          }
          
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Éxito', 
            detail: `Plantilla ${data.name} creada correctamente` 
          });
          this.hideDialog();
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error creating template:', error);
          this.handleError(error);
        }
      });
    } else if (this.action === 'EDIT') {
      this.apiTemplatesService.patchCollection(data, undefined, `${this.apiTemplatesService.path}/${this.selectedTemplateId}`).subscribe({
        next: (r: any) => {
          if (r?.error) {
            return this.messageService.add({ 
              severity: 'error', 
              summary: 'Error', 
              detail: r.msg || 'Error al actualizar la plantilla'
            });
          }
          
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Éxito', 
            detail: `Plantilla ${data.name} actualizada correctamente` 
          });
          this.hideDialog();
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error updating template:', error);
          this.handleError(error);
        }
      });
    }
  }

  activateTemplate(id: string) {
    this.confirmationService.confirm({
      message: '¿Desea activar esta plantilla?',
      header: 'Confirmar Activación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, Activar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.apiTemplatesService.activate(id).subscribe((r: any) => {
          if (r.error) {
            this.messageService.add({ 
              severity: 'error', 
              summary: 'Error', 
              detail: r.msg 
            });
            this.cdr.markForCheck();
            return;
          }
          
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Plantilla Activada', 
            detail: 'La plantilla se activó correctamente' 
          });
          this.cdr.markForCheck();
        });
      }
    });
  }

  deactivateTemplate(id: string) {
    this.confirmationService.confirm({
      message: '¿Desea desactivar esta plantilla?',
      header: 'Confirmar Desactivación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, Desactivar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.apiTemplatesService.deactivate(id).subscribe((r: any) => {
          if (r.error) {
            this.messageService.add({ 
              severity: 'error', 
              summary: 'Error', 
              detail: r.msg 
            });
            this.cdr.markForCheck();
            return;
          }
          
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Plantilla Desactivada', 
            detail: 'La plantilla se desactivó correctamente' 
          });
          this.cdr.markForCheck();
        });
      }
    });
  }

  deleteTemplate(id: string) {
    this.confirmationService.confirm({
      message: '¿Está seguro de eliminar esta plantilla? Esta acción no se puede deshacer.',
      header: 'Confirmar Eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.apiTemplatesService.deleteCollection(id).subscribe((r: any) => {
          if (r.error) {
            this.messageService.add({ 
              severity: 'error', 
              summary: 'Error', 
              detail: r.msg 
            });
            this.cdr.markForCheck();
            return;
          }
          
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Plantilla Eliminada', 
            detail: 'La plantilla se eliminó correctamente' 
          });
          this.cdr.markForCheck();
        });
      }
    });
  }

  /**
   * Maneja errores de forma centralizada
   */
  handleError(error: any): void {
    this.hasError = true;
    this.errorMessage = error.message || 'Ocurrió un error al cargar los datos';
    console.error('Error in TemplatesListComponent:', error);
    
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: this.errorMessage,
      life: 5000
    });
    this.cdr.markForCheck();
  }

  /**
   * Reintenta la operación después de un error
   */
  retry(): void {
    this.hasError = false;
    this.errorMessage = '';
    // Trigger refresh en la tabla (MainTableComponent tiene lógica de refresh)
    window.location.reload();
  }
}
